import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import api from '@/lib/api';
import {
  buildVideoProjectDocument,
  normalizeVideoSubtitleStyle,
  setPendingVideoProjectNavigation,
} from '@/lib/videoEditorProjectFile';

/** Load pitch project editor snapshot and hand off to the shared video editor. */
export function PitchVideoEditorPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/pitch/projects/${projectId}`);
        const project = data.project;
        const storyId = data.story_id || project?.story_id;
        if (!storyId) {
          throw new Error('Pitch project has no linked story');
        }
        const editor = project?.video_editor || {};
        const doc = buildVideoProjectDocument({
          storyId,
          story: {
            id: storyId,
            title: project.title || 'Pitch recording',
            story_type: 'pitch_recording',
            pitch_project_id: project.id,
          },
          scenes: [],
          videoTracks: editor.videoTracks || [[]],
          audioTracks: editor.audioTracks || [[]],
          musicTracks: [[]],
          subtitleTracks: editor.subtitleTracks || [[]],
          subtitleStyle: normalizeVideoSubtitleStyle(null),
          timelineZoom: 1,
          timelineActive: { image: false, video: true, audio: true, music: false, subtitle: true },
          volume: 0.8,
          isMuted: false,
        });
        if (cancelled) return;
        setPendingVideoProjectNavigation(doc);
        navigate(`/stories/${storyId}/video-editor`, { replace: true });
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || err.message || 'Failed to load pitch editor');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, navigate]);

  if (error) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-red-400 text-sm px-4">
        {error}
      </div>
    );
  }

  return (
    <div className="min-h-[40vh] flex flex-col items-center justify-center gap-3 text-slate-400">
      <Loader2 className="w-8 h-8 animate-spin" />
      <p className="text-sm">Opening pitch timeline in editor…</p>
    </div>
  );
}

export default PitchVideoEditorPage;
